import { Component } from '@theme/component';

/**
 * Clamps a block of flowing rich text to a fixed number of lines and reveals the rest on
 * demand.
 *
 * This exists alongside `show-more-component` rather than reusing it: that component toggles
 * a list of discrete items (it requires a `showMoreItems` ref and animates by measuring the
 * height difference once those items are unhidden), which a single continuous block of prose
 * does not have. It is also deliberately not `disclosure-custom`, which marks its content
 * `inert` while collapsed — correct when the content is fully hidden, wrong here, where the
 * first few lines stay on screen and must remain readable by assistive technology.
 *
 * The full text is always present in the DOM, expanded or not, so nothing is hidden from
 * crawlers.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} readMoreButton – The toggle control.
 * @property {HTMLElement} readMoreContent – The clamped text container.
 *
 * @extends Component<Refs>
 */
class ReadMore extends Component {
  requiredRefs = ['readMoreButton', 'readMoreContent'];

  #resizeObserver = new ResizeObserver(() => this.#syncToggleVisibility());

  /**
   * Hides the toggle when the text already fits inside the clamp, so short descriptions
   * don't get a "Read more" control that does nothing.
   */
  #syncToggleVisibility = () => {
    if (this.dataset.expanded === 'true') return;

    const { readMoreContent: content, readMoreButton: button } = this.refs;
    button.hidden = content.scrollHeight - content.clientHeight <= 1;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#syncToggleVisibility();
    this.#resizeObserver.observe(this.refs.readMoreContent);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    super.disconnectedCallback();
  }

  /**
   * Expands or collapses the text. Styling keys off `data-expanded` on the host element.
   *
   * @param {Event} event - The click event
   */
  toggle = (event) => {
    event.preventDefault();

    const expanded = this.dataset.expanded === 'true';
    this.dataset.expanded = String(!expanded);
    this.refs.readMoreButton.setAttribute('aria-expanded', String(!expanded));
  };
}

if (!customElements.get('read-more-component')) {
  customElements.define('read-more-component', ReadMore);
}
